'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

/**
 * App Store / Google Play badge pair — THE web direct-to-app affordance
 * (purchases run through the mobile apps; web is read-only for payments).
 * Localized badge artwork lives in /public/images/badges/{store}/{locale}.svg.
 *
 * Shared by the account-page MobileAppBanner and the pack/Builder Pass
 * paywall surfaces so every "get it in the app" direction looks identical.
 */
export function StoreBadgeLinks({
  className,
  onBadgeClick,
}: {
  className?: string;
  /**
   * Fired when either store badge is tapped. Used by the pack/builder
   * paywalls as the web analog of "purchase started" — the actual checkout
   * happens in the mobile app. Omitted by non-paywall callers (account banner).
   */
  onBadgeClick?: () => void;
}) {
  const t = useTranslations('mobileAppBanner');
  const locale = useLocale();

  const appStoreUrl = process.env.NEXT_PUBLIC_APPSTORE_URL || '#';
  const playStoreUrl = process.env.NEXT_PUBLIC_PLAYSTORE_URL || '#';

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onBadgeClick}
      >
        <Image
          src={`/images/badges/app-store/${locale}.svg`}
          alt={t('appStore')}
          width={120}
          height={40}
        />
      </a>
      <a
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onBadgeClick}
      >
        <Image
          src={`/images/badges/google-play/${locale}.svg`}
          alt={t('googlePlay')}
          width={135}
          height={40}
        />
      </a>
    </div>
  );
}
